import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import './styles.css';
import Home from './pages/Home';
import Signup from './pages/Signup';
import UserLogin from './pages/UserLogin';
import MagicConsume from './pages/MagicConsume';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Dashboard from './pages/Dashboard';
import UserTenantView from './pages/user/UserTenantView';
import Login from './pages/Login';                  // legacy operator (single-password) login
import TenantsView from './pages/TenantsView';
import NewTenant from './pages/NewTenant';
import Reconnect from './pages/Reconnect';
import Clients from './pages/Clients';
import Tokens from './pages/Tokens';
import Settings from './pages/Settings';
import Docs from './docs/Docs';
import AppHome from './pages/AppHome';
import AuditLog from './pages/AuditLog';
import Shell from './components/Shell';
import { useQuery } from '@tanstack/react-query';
import { api } from './lib/api';

const qc = new QueryClient({ defaultOptions: { queries: { staleTime: 5000 } } });


/** Operator-area gate. Allows any authenticated session whose role is
 * 'admin' (god-mode, including the legacy single-password cookie) or
 * 'operator' (workspace owner from /signup). Legacy 'user' rows still
 * bounce to /dashboard. Anonymous → /login. */
function OperatorGuard({ children }: { children: React.ReactNode }) {
  const q = useQuery({
    queryKey: ['users-me'],
    queryFn: api.userMe,
    retry: false,
    staleTime: 30_000,
  });
  if (q.isLoading) return <div className="p-8 text-dim text-sm">Loading…</div>;
  if (q.isError)   return <Navigate to="/login" replace />;
  const m: any = q.data;
  if (!m) return <Navigate to="/login" replace />;
  // Legacy admin cookie / API token responses lack a `role` key but are
  // operator-equivalent. Allow them through.
  if (!('role' in m)) return <>{children}</>;
  if (m.role === 'admin' || m.role === 'operator') return <>{children}</>;
  return <Navigate to="/dashboard" replace />;
}


ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={qc}>
      <BrowserRouter>
        <Routes>
          {/* ── Public surface ── */}
          <Route path="/"        element={<Home />} />
          <Route path="/signup"  element={<Signup />} />
          <Route path="/login"   element={<UserLogin />} />
          <Route path="/auth/magic" element={<MagicConsume />} />
          <Route path="/terms"   element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/admin"   element={<Login />} />     {/* legacy operator single-password */}

          {/* ── Customer dashboard (signed-up users) ── */}
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/dashboard/tenants/:id/*" element={<UserTenantView />} />

          {/* ── Operator area (admin or operator-role users) ── */}
          <Route element={<OperatorGuard><Shell /></OperatorGuard>}>
            <Route path="/home"             element={<AppHome />} />
            <Route path="/tenants"          element={<TenantsView />} />
            <Route path="/tenants/:id/*"    element={<TenantsView />} />
            <Route path="/new"              element={<NewTenant />} />
            <Route path="/reconnect"        element={<Reconnect />} />
            <Route path="/clients"          element={<Clients />} />
            <Route path="/tokens"           element={<Tokens />} />
            <Route path="/audit-log"        element={<AuditLog />} />
            <Route path="/settings"         element={<Settings />} />
            <Route path="/how-to-use"       element={<Navigate to="/docs/getting-started/quick-start" replace />} />
            <Route path="/docs"             element={<Docs />} />
            <Route path="/docs/:section"    element={<Docs />} />
            <Route path="/docs/:section/:page" element={<Docs />} />
          </Route>

          {/* Catch-all → home. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
